import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSwitcherOrganizations } from '@/hooks/useOrganization';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { cn } from '@/lib/utils';
import type { OrganizationSwitcherItem } from '@octave/contracts';
import { Building2, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function OrganizationSwitcher({ className }: { className?: string } = {}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { data: organizations, isLoading, error } = useSwitcherOrganizations();
  const { tenantSlug, saveTenantSlug } = useTenantSlug();
  const navigate = useNavigate();
  const location = useLocation();

  const currentOrganization = organizations?.find(
    (org) => org.tenantSlug === tenantSlug
  );

  const filteredOrganizations = organizations?.filter((org) =>
    org.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleOrganizationSelect = (org: OrganizationSwitcherItem) => {
    saveTenantSlug(org.tenantSlug);

    // Preserve the current path but replace the tenant slug
    const currentPath = location.pathname;
    const newPath = currentPath.replace(/^\/o\/[^/]+/, `/o/${org.tenantSlug}`);

    // If we're on an admin route that doesn't have a tenant slug, navigate to home
    if (currentPath.startsWith('/o/admin/')) {
      navigate(`/o/${org.tenantSlug}/home`);
    } else {
      navigate(newPath);
    }

    setOpen(false);
    setSearchValue('');
    // Reload the page to ensure all data is refreshed
    window.location.reload();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearchValue('');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-sm text-muted-foreground">
          Chargement des organisations...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="text-sm text-destructive">
          Erreur lors du chargement des organisations
        </span>
      </div>
    );
  }

  if (!organizations || organizations.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="text-sm text-muted-foreground">
          Aucune organisation disponible
        </span>
      </div>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn("w-full justify-between px-3 py-2 h-auto", className)}
          aria-label="Switch organization"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="truncate text-sm font-medium">
              {currentOrganization?.name || 'Select Organization'}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Rechercher une organisation..."
            value={searchValue}
            onValueChange={setSearchValue}
            onKeyDown={handleKeyDown}
          />
          <CommandList>
            <CommandEmpty>Aucune organisation trouvée.</CommandEmpty>
            <CommandGroup>
              {filteredOrganizations?.map((org) => (
                <CommandItem
                  key={org.id}
                  onSelect={() => handleOrganizationSelect(org)}
                  className="flex items-center gap-2"
                >
                  <Building2 className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="font-medium">{org.name}</span>
                    {org.description && (
                      <span className="text-xs text-muted-foreground">
                        {org.description}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
